/**
 * The certificate state of one pressure-control component, as the API's
 * certState says it: the status badge, the last valid day, the days remaining
 * and the reason. Shared by the Equipment-detail panel and the component's own
 * page so the two cannot word the same state differently.
 *
 * Nothing is computed here. `expiresOn` and `daysUntilExpiry` are the API's;
 * absent renders `—`.
 */
import React from 'react';
import type { PcCertification, PcCertState } from '../../types/pressure-control';
import { PC_STATUS_FILL, PC_STATUS_LABEL, pcDay, pcDaysPhrase, pcReason } from '../../lib/pressure-control';

/** Saturated fill, dark ink. An unknown status reads `—` on slate, never a real state. */
export function PcStatusBadge({ status }: { status: string | null | undefined }) {
  const label = (status && PC_STATUS_LABEL[status]) || '—';
  const fill = (status && PC_STATUS_FILL[status]) || 'bg-fi-signal-slate';
  return (
    <span
      data-testid="pc-status-badge"
      data-status={status ?? ''}
      className={`inline-flex w-fit whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${fill}`}
    >
      {label}
    </span>
  );
}

function Figure({ label, value, testId }: { label: string; value: React.ReactNode; testId?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</dt>
      <dd data-testid={testId} className="mt-1 text-sm font-semibold text-white">{value}</dd>
    </div>
  );
}

export function PcCertStateSummary({
  state,
  cert,
}: {
  state: PcCertState | null;
  cert: PcCertification | null;
}) {
  return (
    <div className="space-y-3" data-testid="pc-cert-state">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Figure label="Status" value={<PcStatusBadge status={state?.status} />} />
        <Figure label="Valid through" value={pcDay(state?.expiresOn)} testId="pc-valid-through" />
        <Figure label="Days remaining" value={pcDaysPhrase(state?.daysUntilExpiry)} testId="pc-days" />
        <Figure label="Certified" value={pcDay(cert?.certifiedOn)} testId="pc-certified-on" />
      </dl>
      <p className="text-sm text-fieldiq-text" data-testid="pc-reason">{pcReason(state, cert)}</p>
    </div>
  );
}
